import {
  useGetFailedAdjustmentJobsQuery,
  useGetFailedAdjustmentRecordsQuery,
  useLazyDownloadAdjustmentTemplateQuery,
  useUploadAdjustmentsMutation,
  type FailedAdjustmentRecord,
} from '@/app/features/employee/emp-payroll';
import { BulkUploader, BulkUploadErrors } from '@/components/BulkUploader';
import useModal from '@/hooks/useModal';
import { toast } from '@/lib/toast';
import { Button, type TableProps } from 'antd';
import { saveBlobUrl } from '../shared/payroll.helpers';

const columns: TableProps<FailedAdjustmentRecord>['columns'] = [
  { title: 'Row', dataIndex: 'row_number', width: 70 },
  { title: 'Employee ID', dataIndex: 'employee_id', render: (v) => v || 'n/a' },
  { title: 'Name', dataIndex: 'name', render: (v) => v || 'n/a' },
  { title: 'Addition', dataIndex: 'addition', render: (v) => v || 'n/a' },
  { title: 'Deduction', dataIndex: 'deduction', render: (v) => v || 'n/a' },
  { title: 'Tax', dataIndex: 'tax', render: (v) => v || 'n/a' },
  {
    title: 'Failure Reason',
    dataIndex: 'failure_reason',
    render: (v: string) => <span className="text-red-400">{v || 'n/a'}</span>,
  },
];

type Props = {
  open: boolean;
  onClose: () => void;
  /** Batch whose employees the template is built from. */
  batchId: string;
};

/**
 * Bulk addition / deduction / tax for one batch.
 *
 * The template already carries every employee in the batch with their current
 * amounts, so the admin only edits the three amount columns and uploads the
 * same file back — no ids to type, no row-by-row editing.
 */
const AdjustmentUpload = ({ open, onClose, batchId }: Props) => {
  const {
    open: errorModal,
    handleOpen,
    handleClose,
  } = useModal<undefined, 'ERROR_SHOW'>();

  const [downloadTemplate] = useLazyDownloadAdjustmentTemplateQuery();
  const [uploadAdjustments, { isLoading: uploading }] =
    useUploadAdjustmentsMutation();

  const handleDownloadTemplate = async () => {
    try {
      const blobUrl = await downloadTemplate({ batch_id: batchId }).unwrap();
      saveBlobUrl(blobUrl, `payroll-adjustments-${batchId}.xlsx`);
    } catch {
      toast('error', 'Failed to download template.');
    }
  };

  const handleUpload = async (file: File) => {
    await uploadAdjustments({ file, batch_id: batchId }).unwrap();
  };

  return (
    <>
      <BulkUploader
        open={open}
        onClose={onClose}
        title="Upload Adjustments"
        subtitle=".xlsx · edit only the Addition, Deduction and Tax columns"
        uploadLoading={uploading}
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleUpload}
        errorViewer={
          <Button
            size="middle"
            className="btn-primary-bordered !h-10"
            onClick={() => handleOpen({ type: 'ERROR_SHOW' })}
          >
            View Error Table Data
          </Button>
        }
      />

      <BulkUploadErrors<FailedAdjustmentRecord>
        open={errorModal.open && errorModal.type === 'ERROR_SHOW'}
        onClose={handleClose}
        title="Adjustment Upload Errors"
        useGetJobsQuery={useGetFailedAdjustmentJobsQuery as any}
        useGetRecordsQuery={useGetFailedAdjustmentRecordsQuery as any}
        columns={columns}
      />
    </>
  );
};

export default AdjustmentUpload;
